'use client';

import type { ThemeMode } from '@constants/colors';
import { useState, useEffect } from 'react';
import { Modal, Stack, Text, Group, Badge, Timeline, Loader, ThemeIcon, ActionIcon, Tooltip } from '@mantine/core';
import { IconClock, IconCircleCheck, IconHourglass } from '@tabler/icons-react';
import axiosInstance from '@libs/axios';
import { notifications } from '@mantine/notifications';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { getHoverActionIconStyles } from '@components/commons/hover-action-icon-styles';

interface StatusHistoryItem {
  responseName: string;
  responseTime: string;
  leadTime: number;
  fileURL?: string;
}

interface StatusHistoryData {
  drafterName: string;
  companyName: string;
  ajuNo: string;
  items: StatusHistoryItem[];
}

interface StatusHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  ajuNo: string;
  totalItem?: number;
  shipperCountry?: string;
}

// Helper function to format lead time
const formatLeadTime = (seconds: number): string => {
  if (seconds === 0) return '0s';

  const days = Math.floor(seconds / 86400); // 24 * 60 * 60 = 86400 seconds in a day
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (days > 0) {
    // If more than 24 hours, show in days
    if (hours > 0) {
      return `${days}d ${hours}h`;
    }
    return `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

// Helper function to format response time
const formatResponseTime = (dateTime: string): { date: string; time: string } => {
  const date = new Date(dateTime);
  return {
    date: date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
  };
};

// Get status color using project color scheme
const STATUS_COLORS = {
  DRAFT: {
    light: { bg: '#E2E8F0', text: '#1E293B', border: '#94A3B8' },
    dark: { bg: '#1F2933', text: '#E2E8F0', border: '#475569' },
  },
  'DRAFT SUBMITTED': {
    light: { bg: '#F3E8FF', text: '#6B21A8', border: '#C084FC' },
    dark: { bg: '#362148', text: '#E9D5FF', border: '#A855F7' },
  },
  UMUM: {
    light: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
    dark: { bg: '#1F3D2B', text: '#BBF7D0', border: '#22C55E' },
  },
  NPE: {
    light: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
    dark: { bg: '#4C1D1D', text: '#FECACA', border: '#F87171' },
  },
  SPPB: {
    light: { bg: '#DBF4FF', text: '#065F46', border: '#7DD3FC' },
    dark: { bg: '#0F2E3D', text: '#CFFAFE', border: '#14B8A6' },
  },
} as const;

const getStatusStyle = (status: string, mode: ThemeMode) => {
  const normalized = status.toUpperCase() as keyof typeof STATUS_COLORS;
  const fallback = {
    light: { bg: '#E2E8F0', text: '#1E293B', border: '#94A3B8' },
    dark: { bg: '#1F2933', text: '#E2E8F0', border: '#475569' },
  };
  const palette = STATUS_COLORS[normalized] ?? fallback;

  // Map all themed modes to light/dark styles
  // Pink / horizon / dawn / mist / jarvis are light; horizon-night, dusk, jarvis-night and other dark palettes use dark chips
  const effectiveMode: 'light' | 'dark' =
    mode === 'cream' || mode === 'pink' || mode === 'horizon' || mode === 'dawn' || mode === 'mist' || mode === 'jarvis'
      ? 'light'
      : mode === 'blue' ||
          mode === 'navy' ||
          mode === 'purple' ||
          mode === 'green' ||
          mode === 'red' ||
          mode === 'orange' ||
          mode === 'jarvis-classic' ||
          mode === 'horizon-night' ||
          mode === 'dusk' ||
          mode === 'jarvis-night'
        ? 'dark'
        : mode;
  return palette[effectiveMode];
};

// Get status icon - use checkmark for all statuses after DRAFT
const getStatusIcon = (status: string, isFirstStatus = false) => {
  if (isFirstStatus && status.toUpperCase() === 'DRAFT') {
    return <IconHourglass size={16} />;
  }

  // All other statuses use checkmark
  return <IconCircleCheck size={16} />;
};

export default function StatusHistoryModal({
  opened,
  onClose,
  ajuNo,
  totalItem,
  shipperCountry,
}: StatusHistoryModalProps) {
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StatusHistoryData | null>(null);

  const fetchStatusHistory = async () => {
    if (!ajuNo || !opened) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/get-history-status?ajuNo=${ajuNo}`);

      if (response.data?.success) {
        setData(response.data.data);
      } else {
        notifications.show({ title: 'Error', message: t('statushistory.errFetch'), color: 'red' });
        onClose();
      }
    } catch (error) {
      console.error('Error fetching status history:', error);
      notifications.show({ title: 'Error', message: t('statushistory.errGeneric'), color: 'red' });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatusHistory();
  }, [opened, ajuNo]);

  // Reset data when modal closes
  useEffect(() => {
    if (!opened) {
      setData(null);
    }
  }, [opened]);

  const formatAjuNumber = (value: string) => {
    if (!value) return '-';
    // Remove any existing dashes to get raw value
    const rawValue = value.replace(/-/g, '');
    // Format: 6-6-8-6 characters with dashes
    let formatted = rawValue;
    if (rawValue.length > 6) {
      formatted = `${rawValue.slice(0, 6)}-${rawValue.slice(6)}`;
    }
    if (rawValue.length > 12) {
      formatted = `${rawValue.slice(0, 6)}-${rawValue.slice(6, 12)}-${rawValue.slice(12)}`;
    }
    if (rawValue.length > 20) {
      formatted = `${rawValue.slice(0, 6)}-${rawValue.slice(6, 12)}-${rawValue.slice(12, 20)}-${rawValue.slice(20)}`;
    }
    return formatted;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon
            variant="light"
            size="sm"
            style={{
              backgroundColor: themeColors.formFields,
              color: themeColors.text,
            }}
          >
            <IconClock size={14} />
          </ThemeIcon>
          <Text size="md" fw={600} style={{ color: themeColors.text }}>
            {t('statushistory.title')}
          </Text>
        </Group>
      }
      size="md"
      centered
      overlayProps={{
        backgroundOpacity: 0.6,
        blur: 2,
      }}
      styles={{
        content: {
          background: themeColors.card,
          borderRadius: '8px',
          border: `1px solid ${themeColors.border}`,
          boxShadow:
            mode === 'light'
              ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              : '0 20px 40px rgba(0, 0, 0, 0.6)',
        },
        header: {
          borderBottom: `1px solid ${themeColors.border}`,
          paddingBottom: '0.75rem',
          marginBottom: '1rem',
          backgroundColor: themeColors.card,
        },
        body: {
          padding: '1rem',
          backgroundColor: themeColors.card,
        },
      }}
    >
      {loading ? (
        <Group justify="center" py="lg">
          <Loader size="md" />
          <Text size="xs" c="dimmed">
            {t('statushistory.loading')}
          </Text>
        </Group>
      ) : data ? (
        <Stack gap="sm">
          {/* Document Information - Compact */}
          <div
            className="rounded-[8px] p-3 shadow-sm"
            style={{
              backgroundColor: themeColors.formFields,
              border: `1px solid ${themeColors.border}`,
              boxShadow: mode === 'light' ? '0 1px 3px rgba(15, 23, 42, 0.05)' : '0 0 0 1px rgba(148,163,184,0.1)',
            }}
          >
            <Group justify="space-between" mb={4}>
              <Text size="xs" fw={500} style={{ color: themeColors.text }}>
                {t('statushistory.document')}
              </Text>
              <Text size="xs" fw={600} ff="monospace" style={{ color: themeColors.text }}>
                {formatAjuNumber(data.ajuNo)}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" style={{ color: themeColors.textSecondary }}>
                {data.companyName}
              </Text>
              <Text size="xs" style={{ color: themeColors.textSecondary }}>
                {t('statushistory.by')} {data.drafterName}
              </Text>
            </Group>
            {(totalItem || shipperCountry) && (
              <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${themeColors.border}` }}>
                {totalItem && (
                  <Group justify="space-between" mb={2}>
                    <Text size="xs" fw={500} style={{ color: themeColors.text }}>
                      {t('statushistory.totalItems')}
                    </Text>
                    <Badge
                      size="xs"
                      variant="light"
                      styles={{
                        root: {
                          backgroundColor: themeColors.formFields,
                          borderColor: themeColors.border,
                          color: themeColors.text,
                        },
                      }}
                    >
                      {totalItem} {totalItem === 1 ? t('statushistory.item') : t('statushistory.items')}
                    </Badge>
                  </Group>
                )}
                {shipperCountry && (
                  <Group justify="space-between">
                    <Text size="xs" fw={500} style={{ color: themeColors.text }}>
                      {t('statushistory.country')}
                    </Text>
                    <Badge
                      size="xs"
                      variant="light"
                      styles={{
                        root: {
                          backgroundColor: `${themeColors.info}20`,
                          borderColor: themeColors.info,
                          color: themeColors.text,
                          border: `1px solid ${themeColors.info}`,
                        },
                      }}
                    >
                      {shipperCountry.toUpperCase()}
                    </Badge>
                  </Group>
                )}
              </div>
            )}
          </div>

          {/* Compact Timeline */}
          <Timeline
            bulletSize={20}
            lineWidth={3}
            color={themeColors.textSecondary}
            styles={{
              itemBullet: {
                border: `2px solid ${themeColors.card}`,
                boxShadow: mode === 'light' ? '0 1px 4px rgba(0,0,0,0.1)' : '0 0 0 1px rgba(148,163,184,0.2)',
                backgroundColor: themeColors.card,
              },
              itemTitle: {
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '1.2',
              },
              itemBody: {
                paddingTop: '2px',
                marginBottom: '8px',
              },
              item: {
                paddingBottom: '12px',
              },
            }}
          >
            {data.items.map((item, index) => {
              const { date, time } = formatResponseTime(item.responseTime);
              const statusStyle = getStatusStyle(item.responseName, mode);

              return (
                <Timeline.Item
                  key={`${item.responseTime}|${item.responseName}|${item.leadTime}|${item.fileURL ?? ''}`}
                  bullet={
                    <ThemeIcon
                      size={14}
                      radius="xl"
                      variant="filled"
                      style={{
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.text,
                        border: `1px solid ${statusStyle.border}`,
                      }}
                    >
                      {getStatusIcon(item.responseName, index === 0)}
                    </ThemeIcon>
                  }
                  title={
                    <Group justify="space-between" align="center">
                      <Text size="xs" fw={600} style={{ color: themeColors.text }}>
                        {item.responseName}
                      </Text>
                      {item.leadTime > 0 && (
                        <Badge
                          size="xs"
                          variant="light"
                          styles={{
                            root: {
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.text,
                              border: `1px solid ${statusStyle.border}`,
                              fontSize: '10px',
                              height: '18px',
                              paddingLeft: '6px',
                              paddingRight: '6px',
                            },
                          }}
                        >
                          {formatLeadTime(item.leadTime)}
                        </Badge>
                      )}
                    </Group>
                  }
                >
                  <Group gap="xs" align="center">
                    <Text size="xs" style={{ color: themeColors.textSecondary }}>
                      {date} • {time}
                    </Text>
                    {item.fileURL && (
                      <Badge
                        size="xs"
                        variant="light"
                        styles={{
                          root: {
                            backgroundColor: `${themeColors.info}20`,
                            borderColor: themeColors.info,
                            color: themeColors.text,
                            border: `1px solid ${themeColors.info}`,
                          },
                        }}
                      >
                        {t('statushistory.file')}
                      </Badge>
                    )}
                  </Group>
                </Timeline.Item>
              );
            })}
          </Timeline>

          {/* Compact Summary */}
          <div
            className="rounded-[8px] p-3 shadow-sm"
            style={{
              backgroundColor: themeColors.formFields,
              border: `1px solid ${themeColors.border}`,
              boxShadow: mode === 'light' ? '0 1px 3px rgba(15, 23, 42, 0.05)' : '0 0 0 1px rgba(148,163,184,0.1)',
            }}
          >
            <Group justify="space-between" align="center">
              <Text size="xs" fw={500} style={{ color: themeColors.text }}>
                {t('statushistory.totalTime')}
              </Text>
              <Badge
                size="sm"
                variant="light"
                styles={{
                  root: {
                    backgroundColor: `${themeColors.primary}20`,
                    borderColor: themeColors.primary,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.primary}`,
                  },
                }}
              >
                {data.items.length > 0
                  ? formatLeadTime(data.items.reduce((total, item) => total + item.leadTime, 0))
                  : '0s'}
              </Badge>
            </Group>
          </div>
        </Stack>
      ) : (
        <Group justify="center" py="lg">
          <Text size="xs" c="dimmed">
            {t('statushistory.noData')}
          </Text>
        </Group>
      )}
    </Modal>
  );
}

export function StatusHistoryIconButton({
  ajuNo,
  totalItem,
  shipperCountry,
}: {
  ajuNo: string;
  totalItem?: number;
  shipperCountry?: string;
}) {
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Tooltip label={t('statushistory.viewResponHistory')}>
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={() => setOpened(true)}
          styles={getHoverActionIconStyles(themeColors, themeColors.warning)}
        >
          <IconClock size={16} />
        </ActionIcon>
      </Tooltip>

      <StatusHistoryModal
        opened={opened}
        onClose={() => setOpened(false)}
        ajuNo={ajuNo}
        totalItem={totalItem}
        shipperCountry={shipperCountry}
      />
    </>
  );
}
